import { ActivityType } from '../types/activity.types';

/**
 * Activity Matcher Interface
 * Defines how to match an API call to an activity type
 */
interface ActivityMatcher {
  pattern: RegExp;
  method: string | string[];
  activityType: ActivityType;
  action: string;
  targetType: string;
  extractTargetName?: (response: any, requestData?: any) => string;
  extractTargetId?: (response: any, requestData?: any) => string | null;
  extractMetadata?: (response: any, requestData?: any) => any;
  shouldSkip?: (response: any, requestData?: any) => boolean;
}

/**
 * Activity Matchers
 * Array of matchers to classify API calls into activity types
 */
export const activityMatchers: ActivityMatcher[] = [
  // Product activities
  {
    pattern: /^\/superAdmin\/products$/,
    method: 'POST',
    activityType: ActivityType.PRODUCT_CREATED,
    action: 'created',
    targetType: 'product',
    extractTargetName: (res) => res?.data?.data?.productName || res?.data?.productName || 'Unknown Product',
    extractTargetId: (res) => res?.data?.data?._id || res?.data?._id || null,
    extractMetadata: (res) => ({
      price: res?.data?.data?.offerPrice || res?.data?.offerPrice,
      category: res?.data?.data?.category || res?.data?.category,
    }),
  },
  {
    pattern: /^\/superAdmin\/products\/[^/]+$/,
    method: 'PUT',
    activityType: ActivityType.PRODUCT_UPDATED,
    action: 'updated',
    targetType: 'product',
    extractTargetName: (res) => res?.data?.data?.productName || res?.data?.productName || 'Product',
    extractTargetId: (res, req) => req?.productId || res?.data?.data?._id || res?.data?._id || null,
  },
  {
    pattern: /^\/superAdmin\/products\/[^/]+$/,
    method: 'DELETE',
    activityType: ActivityType.PRODUCT_DELETED,
    action: 'deleted',
    targetType: 'product',
    extractTargetName: () => 'Product',
    extractTargetId: (res, req) => req?.productId || null,
  },
  {
    pattern: /^\/superAdmin\/products\/[^/]+\/images$/,
    method: 'POST',
    activityType: ActivityType.PRODUCT_IMAGE_UPLOADED,
    action: 'uploaded',
    targetType: 'product',
    extractTargetName: (res) => res?.data?.data?.productName || 'Product',
    extractTargetId: (res, req) => req?.productId || null,
  },

  // Order activities
  {
    pattern: /^\/user\/orders$/,
    method: 'POST',
    activityType: ActivityType.ORDER_CREATED,
    action: 'created',
    targetType: 'order',
    extractTargetName: (res) => `Order #${res?.data?.data?.orderId || res?.data?.orderId || 'Unknown'}`,
    extractTargetId: (res) => res?.data?.data?._id || res?.data?._id || null,
    extractMetadata: (res) => ({
      amount: res?.data?.data?.totalAmount || res?.data?.totalAmount,
      itemCount: res?.data?.data?.items?.length || 0,
    }),
  },
  {
    pattern: /^\/superAdmin\/orders\/[^/]+$/,
    method: 'PUT',
    activityType: ActivityType.ORDER_UPDATED,
    action: 'updated',
    targetType: 'order',
    extractTargetName: (res) => `Order #${res?.data?.data?.orderId || res?.data?.orderId || 'Unknown'}`,
    extractTargetId: (res, req) => req?.orderId || null,
  },
  {
    pattern: /^\/superAdmin\/orders\/[^/]+\/status$/,
    method: ['PUT', 'PATCH'],
    activityType: ActivityType.ORDER_STATUS_CHANGED,
    action: 'changed',
    targetType: 'order',
    extractTargetName: (res) => `Order #${res?.data?.data?.orderId || res?.data?.orderId || 'Unknown'}`,
    extractTargetId: (res, req) => req?.orderId || null,
    extractMetadata: (res, req) => ({
      status: req?.status || res?.data?.data?.status || res?.data?.status,
    }),
  },
  {
    pattern: /^\/user\/orders\/[^/]+\/cancel$/,
    method: ['POST', 'PUT'],
    activityType: ActivityType.ORDER_CANCELLED,
    action: 'cancelled',
    targetType: 'order',
    extractTargetName: (res) => `Order #${res?.data?.data?.orderId || 'Unknown'}`,
    extractTargetId: (res, req) => req?.orderId || null,
  },

  // Customer activities
  {
    pattern: /^\/auth\/signup$/,
    method: 'POST',
    activityType: ActivityType.CUSTOMER_REGISTERED,
    action: 'created',
    targetType: 'customer',
    extractTargetName: (res, req) => `${req?.firstName || ''} ${req?.lastName || ''}`.trim() || 'Customer',
    extractTargetId: (res) => res?.data?.data?._id || res?.data?._id || null,
  },
  {
    pattern: /^\/superAdmin\/customers\/[^/]+$/,
    method: 'PUT',
    activityType: ActivityType.CUSTOMER_UPDATED,
    action: 'updated',
    targetType: 'customer',
    extractTargetName: (res) => res?.data?.data?.name || 'Customer',
    extractTargetId: (res, req) => req?.customerId || null,
  },
  {
    pattern: /^\/superAdmin\/customers\/[^/]+$/,
    method: 'DELETE',
    activityType: ActivityType.CUSTOMER_DELETED,
    action: 'deleted',
    targetType: 'customer',
    extractTargetName: () => 'Customer',
    extractTargetId: (res, req) => req?.customerId || null,
  },

  // Organization activities
  {
    pattern: /^\/superAdmin\/organizations$/,
    method: 'POST',
    activityType: ActivityType.ORGANIZATION_CREATED,
    action: 'created',
    targetType: 'organization',
    extractTargetName: (res) => res?.data?.data?.organizationName || res?.data?.organizationName || 'Organization',
    extractTargetId: (res) => res?.data?.data?._id || res?.data?._id || null,
  },
  {
    pattern: /^\/superAdmin\/organizations\/[^/]+$/,
    method: 'PUT',
    activityType: ActivityType.ORGANIZATION_UPDATED,
    action: 'updated',
    targetType: 'organization',
    extractTargetName: (res) => res?.data?.data?.organizationName || 'Organization',
    extractTargetId: (res, req) => req?.organizationId || null,
  },
  {
    pattern: /^\/superAdmin\/organizations\/[^/]+$/,
    method: 'DELETE',
    activityType: ActivityType.ORGANIZATION_DELETED,
    action: 'deleted',
    targetType: 'organization',
    extractTargetName: () => 'Organization',
    extractTargetId: (res, req) => req?.organizationId || null,
  },

  // Employee activities
  {
    pattern: /^\/admin\/employees$/,
    method: 'POST',
    activityType: ActivityType.EMPLOYEE_ADDED,
    action: 'created',
    targetType: 'employee',
    extractTargetName: (res, req) => `${req?.firstName || ''} ${req?.lastName || ''}`.trim() || 'Employee',
    extractTargetId: (res) => res?.data?.data?._id || res?.data?._id || null,
  },
  {
    pattern: /^\/admin\/employees\/[^/]+$/,
    method: 'PUT',
    activityType: ActivityType.EMPLOYEE_UPDATED,
    action: 'updated',
    targetType: 'employee',
    extractTargetName: (res) => res?.data?.data?.name || 'Employee',
    extractTargetId: (res, req) => req?.employeeId || null,
  },
  {
    pattern: /^\/admin\/employees\/[^/]+$/,
    method: 'DELETE',
    activityType: ActivityType.EMPLOYEE_REMOVED,
    action: 'deleted',
    targetType: 'employee',
    extractTargetName: () => 'Employee',
    extractTargetId: (res, req) => req?.employeeId || null,
  },

  // Category activities
  {
    pattern: /^\/superAdmin\/categories$/,
    method: 'POST',
    activityType: ActivityType.CATEGORY_CREATED,
    action: 'created',
    targetType: 'category',
    extractTargetName: (res, req) => req?.categoryName || res?.data?.data?.categoryName || 'Category',
    extractTargetId: (res) => res?.data?.data?._id || res?.data?._id || null,
  },
  {
    pattern: /^\/superAdmin\/categories\/[^/]+$/,
    method: 'PUT',
    activityType: ActivityType.CATEGORY_UPDATED,
    action: 'updated',
    targetType: 'category',
    extractTargetName: (res, req) => req?.categoryName || 'Category',
    extractTargetId: (res, req) => req?.categoryId || null,
  },
  {
    pattern: /^\/superAdmin\/categories\/[^/]+$/,
    method: 'DELETE',
    activityType: ActivityType.CATEGORY_DELETED,
    action: 'deleted',
    targetType: 'category',
    extractTargetName: () => 'Category',
    extractTargetId: (res, req) => req?.categoryId || null,
  },

  // Cart activities
  {
    pattern: /^\/user\/cart\/add$/,
    method: 'POST',
    activityType: ActivityType.CART_ITEM_ADDED,
    action: 'created',
    targetType: 'cart',
    extractTargetName: (res, req) => req?.productName || 'Item',
    extractTargetId: (res, req) => req?.productId || null,
    extractMetadata: (res, req) => ({
      quantity: req?.quantity || 1,
    }),
  },
  {
    pattern: /^\/user\/cart\/remove\/[^/]+$/,
    method: ['DELETE', 'POST'],
    activityType: ActivityType.CART_ITEM_REMOVED,
    action: 'deleted',
    targetType: 'cart',
    extractTargetName: () => 'Item',
    extractTargetId: (res, req) => req?.productId || null,
  },
  {
    pattern: /^\/user\/cart$/,
    method: 'PUT',
    activityType: ActivityType.CART_UPDATED,
    action: 'updated',
    targetType: 'cart',
    extractTargetName: () => 'Cart',
    extractTargetId: () => null,
  },

  // Peer recognition activities
  {
    pattern: /^\/user\/recognition\/send$/,
    method: 'POST',
    activityType: ActivityType.PEER_RECOGNITION_SENT,
    action: 'sent',
    targetType: 'recognition',
    extractTargetName: (res, req) => req?.receiverId ? 'Colleague' : 'Recognition',
    extractTargetId: (res) => res?.data?.recognition?._id || null,
    extractMetadata: (res, req) => ({
      greenCoins: req?.greenCoins,
      tags: req?.tags,
    }),
  },

  // Recognition task activities
  {
    pattern: /^\/admin\/recognition-tasks$/,
    method: 'POST',
    activityType: ActivityType.RECOGNITION_TASK_CREATED,
    action: 'created',
    targetType: 'recognition_task',
    extractTargetName: (res, req) => req?.taskName || res?.data?.data?.taskName || 'Task',
    extractTargetId: (res) => res?.data?.data?._id || res?.data?._id || null,
  },
  {
    pattern: /^\/user\/recognition-tasks\/[^/]+\/complete$/,
    method: ['POST', 'PUT'],
    activityType: ActivityType.RECOGNITION_TASK_COMPLETED,
    action: 'completed',
    targetType: 'recognition_task',
    extractTargetName: (res) => res?.data?.data?.taskName || 'Task',
    extractTargetId: (res, req) => req?.taskId || null,
  },
  {
    pattern: /^\/user\/rewards\/claim$/,
    method: 'POST',
    activityType: ActivityType.REWARD_CLAIMED,
    action: 'claimed',
    targetType: 'reward',
    extractTargetName: (res, req) => req?.rewardName || 'Reward',
    extractTargetId: (res, req) => req?.rewardId || null,
  },

  // Settings activities
  {
    pattern: /^\/superAdmin\/settings$/,
    method: ['PUT', 'PATCH'],
    activityType: ActivityType.SETTINGS_UPDATED,
    action: 'updated',
    targetType: 'settings',
    extractTargetName: () => 'Settings',
    extractTargetId: () => null,
  },

  // Banner activities
  {
    pattern: /^\/superAdmin\/settings\/banners$/,
    method: 'POST',
    activityType: ActivityType.BANNER_CREATED,
    action: 'created',
    targetType: 'banner',
    extractTargetName: (res, req) => req?.title || 'Banner',
    extractTargetId: (res) => res?.data?.data?._id || res?.data?._id || null,
  },
  {
    pattern: /^\/superAdmin\/settings\/banners\/[^/]+$/,
    method: 'PUT',
    activityType: ActivityType.BANNER_UPDATED,
    action: 'updated',
    targetType: 'banner',
    extractTargetName: (res, req) => req?.title || 'Banner',
    extractTargetId: (res, req) => req?.bannerId || null,
  },
  {
    pattern: /^\/superAdmin\/settings\/banners\/[^/]+$/,
    method: 'DELETE',
    activityType: ActivityType.BANNER_DELETED,
    action: 'deleted',
    targetType: 'banner',
    extractTargetName: () => 'Banner',
    extractTargetId: (res, req) => req?.bannerId || null,
  },
];

/**
 * Match Activity
 * Matches an API call to an activity type
 * @param url - API endpoint URL
 * @param method - HTTP method
 * @param response - API response
 * @param requestData - Request data (optional)
 */
export const matchActivity = (
  url: string,
  method: string,
  response: any,
  requestData?: any
): {
  activityType: ActivityType;
  action: string;
  targetType: string;
  targetName: string;
  targetId: string | null;
  metadata?: any;
} | null => {
  // Normalize URL (remove query params and base URL)
  const normalizedUrl = url.replace(/\?.*$/, '');

  for (const matcher of activityMatchers) {
    // Check if method matches
    const methodMatches = Array.isArray(matcher.method)
      ? matcher.method.includes(method.toUpperCase())
      : matcher.method.toUpperCase() === method.toUpperCase();

    if (!methodMatches) continue;

    // Check if URL matches pattern
    if (!matcher.pattern.test(normalizedUrl)) continue;

    // Check if should skip
    if (matcher.shouldSkip && matcher.shouldSkip(response, requestData)) {
      return null;
    }

    // Extract activity data
    const targetName = matcher.extractTargetName
      ? matcher.extractTargetName(response, requestData)
      : matcher.targetType;
    const targetId = matcher.extractTargetId
      ? matcher.extractTargetId(response, requestData)
      : null;
    const metadata = matcher.extractMetadata
      ? matcher.extractMetadata(response, requestData)
      : undefined;

    return {
      activityType: matcher.activityType,
      action: matcher.action,
      targetType: matcher.targetType,
      targetName,
      targetId,
      metadata,
    };
  }

  return null;
};
